import React, { Fragment, useMemo } from 'react'
import { useReactiveVar } from '@apollo/client'

import { useCheckoutRouter } from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { screenResolutionVar } from '@src/models/screenResolution'
import { TreeOutletMenuItemGroup } from '@src/pages/OutletPage/menuItemGroupTreeType'

import { MenuItem } from '../MenuItem/MenuItem'
import {
  ItemContainer,
  ItemsContainer,
  Header,
  HeaderContainer,
  Description,
  ScrollToContainer,
  SubMenuHeader,
} from './MenuGroup.styles'

import { isFinalElementInBreakpoint } from '../isFinalElementInBreakpoint'

export const MenuGroup: React.VFC<{
  outletId: string
  outletParentMenu: TreeOutletMenuItemGroup
  outletSubMenus: TreeOutletMenuItemGroup[]
}> = ({ outletId, outletParentMenu, outletSubMenus }) => {
  const { width } = useReactiveVar(screenResolutionVar)
  const { displayPermanentCheckout } = useCheckoutRouter()

  const renderedMenuItems = useMemo(
    () =>
      outletParentMenu.outletMenuItems.map((menuItem, i) => (
        <ItemContainer key={menuItem.id}>
          <MenuItem
            dataTestId={`outlet-menu-container-item${i}`}
            outletId={outletId}
            menuItem={menuItem}
            menuItemId={menuItem.menuItemId}
            finalElement={isFinalElementInBreakpoint(
              outletParentMenu.outletMenuItems.length,
              i,
              width,
              displayPermanentCheckout
            )}
          />
        </ItemContainer>
      )),
    [
      outletParentMenu.outletMenuItems,
      outletId,
      width,
      displayPermanentCheckout,
    ]
  )

  const renderedSubMenus = useMemo(
    () =>
      outletSubMenus.map(subMenu => (
        <div key={subMenu.id}>
          <SubMenuHeader>
            {subMenu.name}
            {subMenu.description && (
              <Description>{subMenu.description}</Description>
            )}
          </SubMenuHeader>
          <ItemsContainer>
            {subMenu.outletMenuItems.map((menuItem, index) => (
              <ItemContainer key={menuItem.id}>
                <MenuItem
                  outletId={outletId}
                  menuItem={menuItem}
                  menuItemId={menuItem.menuItemId}
                  finalElement={isFinalElementInBreakpoint(
                    subMenu.outletMenuItems.length,
                    index,
                    width,
                    displayPermanentCheckout
                  )}
                />
              </ItemContainer>
            ))}
          </ItemsContainer>
        </div>
      )),
    [outletSubMenus, outletId, width, displayPermanentCheckout]
  )

  return (
    <Fragment>
      <ScrollToContainer id={outletParentMenu.id} aria-hidden />
      <HeaderContainer hasDescription>
        <Header hasDescription>{outletParentMenu.name}</Header>
        {outletParentMenu.description && (
          <Description>{outletParentMenu.description}</Description>
        )}
      </HeaderContainer>

      <ItemsContainer>{renderedMenuItems}</ItemsContainer>

      {renderedSubMenus}
    </Fragment>
  )
}
